/*
  Окно «Кому и как предъявляется требование» - решение владельца 26.10.2026,
  макет Б2.

  Должники счёта и люди из домовой книги одной таблицей; у каждого отметка и
  способ взыскания. Под таблицей - что получится: заявления, к кому они пойдут,
  какие доли не взыскиваются, предупреждения. Итог считает сервер тем же ядром,
  что соберёт пакет, - окно его только показывает.

  Выбор хранится на счёте: по нему собираются пакет, расчёт, уведомление
  каждому отмеченному и иск. Ошибка записи - внутри окна, рядом с кнопкой
  (правило 10 скилла `frontend`).
*/

import { Notice } from './Common.jsx'
import ClaimChoiceRow, { HOW_HEAD_ID } from './ClaimChoiceRow.jsx'
import Modal from './Modal.jsx'
import { useClaimDraft } from './useClaimDraft.js'

const NOTE =
  'Выбор сохраняется на счёте: по нему собираются пакет, расчёт, уведомление каждому ' +
  'отмеченному и иск. Ночной обмен с 1С его не перепишет.'

/** Собственники с долей - те, вместе с кем можно отвечать «солидарно с собственником». */
function holdersOf(people, picks) {
  return Object.fromEntries(
    people
      .filter((person) => picks[person.key].mode === 'share')
      .map((person) => [
        person.key,
        `${person.full_name}${picks[person.key].share ? `, доля ${picks[person.key].share}` : ''}`,
      ]),
  )
}

function Summary({ preview, error }) {
  if (error) return <Notice error={error} />
  if (!preview) return <div className="muted">Считаю, что получится...</div>
  return (
    <div className="modal__text">
      {preview.error ? (
        <Notice kind="error">{preview.error}</Notice>
      ) : (
        <>
          <div className="section__title">
            Заявлений: {preview.claims.length}, пошлин: {preview.claims.length}
          </div>
          <ol>
            {preview.claims.map((line, index) => (
              <li key={`${line.title}:${line.debtors.join('|')}`}>
                Заявление {index + 1} ({line.title}): {line.debtors.join(', ')}
                {line.debtors.length > 1 ? ' - солидарно' : ''}.
              </li>
            ))}
          </ol>
          {preview.dropped.map((text) => (
            <div key={text} className="muted">
              {text}
            </div>
          ))}
        </>
      )}
      {preview.warnings.map((text) => (
        <Notice key={text} kind="warn">
          {text}
        </Notice>
      ))}
    </div>
  )
}

export default function ClaimChoiceDialog({ accountId, accountNumber, choice, asOf, onClose, onSaved }) {
  const draft = useClaimDraft(accountId, choice.people, asOf)
  const holders = holdersOf(choice.people, draft.picks)
  const blocked = Boolean(choice.locked) || !draft.preview || Boolean(draft.preview.error)

  async function save() {
    const saved = await draft.save()
    if (saved) onSaved(saved)
  }

  return (
    <Modal
      title="Кому и как предъявляется требование"
      note={`Счёт № ${accountNumber}`}
      wide
      scroll
      // Запись выбора в пути - Esc и нажатие мимо окно не закрывают, иначе отказ показать
      // негде (ревью 30.10.2026, аудит 5.3 «Окна»).
      busy={draft.saving}
      onClose={onClose}
    >
      <div className="muted">{NOTE}</div>
      {choice.locked ? <Notice kind="warn">{choice.locked}</Notice> : null}
      <div className="modal__table">
        <table className="table">
          <thead>
            <tr>
              <th aria-label="Предъявлять требование" />
              <th>Человек</th>
              <th>Дата рождения</th>
              <th>Кем приходится</th>
              <th>Откуда</th>
              <th id={HOW_HEAD_ID}>Как взыскивать</th>
            </tr>
          </thead>
          <tbody>
            {choice.people.map((person) => (
              <ClaimChoiceRow
                key={person.key}
                person={person}
                pick={draft.picks[person.key]}
                holders={holders}
                onChange={(patch) => draft.change(person.key, patch)}
                locked={choice.locked}
              />
            ))}
          </tbody>
        </table>
      </div>
      <Summary preview={draft.preview} error={draft.previewError} />
      <Notice error={draft.saveError} />
      <div className="row row--tight">
        <button className="button" type="button" onClick={save} disabled={draft.saving || blocked}>
          Сохранить выбор
        </button>
        <button className="button button--quiet" type="button" onClick={onClose} disabled={draft.saving}>
          Отмена
        </button>
      </div>
    </Modal>
  )
}
